import { Building2, Link2, User, UserPlus, X } from "lucide-react";
import { useGetMany, useTranslate, type Identifier } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { RecordPicker } from "../../misc/RecordPicker";
import type { Company, Contact, Partner } from "../../types";
import { contactDraftFromSender } from "../contactDraft";
import type { MailboxMessage } from "../mailTypes";
import { useAddMailboxMessageToCrm } from "./useMailbox";

const contactName = (contact: Contact) =>
  `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();

const useRecords = <T extends { id: Identifier }>(
  resource: string,
  ids: Identifier[],
) => useGetMany<T>(resource, { ids }, { enabled: ids.length > 0 }).data ?? [];

/**
 * What the CRM knows of the message's addresses, with « Ajouter au fil » and,
 * for an unknown sender, « Créer le contact ».
 */
export const MessageCrmPanel = ({
  message,
  ownEmail,
}: {
  message: MailboxMessage;
  ownEmail: string;
}) => {
  const translate = useTranslate();
  const [isAdding, setAdding] = useState(false);
  const { contact_ids, company_ids, partner_ids } = message.links;
  const contacts = useRecords<Contact>("contacts", contact_ids);
  const companies = useRecords<Company>("companies", company_ids);
  const partners = useRecords<Partner>("partners", partner_ids);
  const sender = message.from;
  const isFromSomeoneElse = !!sender && sender.email !== ownEmail.toLowerCase();
  const canCreateContact =
    isFromSomeoneElse && contact_ids.length === 0 && partner_ids.length === 0;
  const links = [
    ...contacts.map((record) => ({
      key: `contact-${record.id}`,
      to: `/contacts/${record.id}/show`,
      label: contactName(record),
      icon: User,
    })),
    ...companies.map((record) => ({
      key: `company-${record.id}`,
      to: `/companies/${record.id}/show`,
      label: record.name,
      icon: Building2,
    })),
    ...partners.map((record) => ({
      key: `partner-${record.id}`,
      to: `/partners/${record.id}/show`,
      label: record.name,
      icon: User,
    })),
  ];

  return (
    <section
      aria-label={translate("crm.mailbox.in_crm")}
      className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2 text-sm"
    >
      {links.length > 0 ? (
        links.map(({ key, to, label, icon: Icon }) => (
          <Link
            key={key}
            to={to}
            className="flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 hover:bg-muted"
          >
            <Icon className="size-3.5 text-muted-foreground" />
            {label}
          </Link>
        ))
      ) : (
        <span className="text-muted-foreground">
          {translate("crm.mailbox.no_record")}
        </span>
      )}
      {message.crm_email_id != null ? (
        <Badge variant="secondary">{translate("crm.mailbox.in_feed")}</Badge>
      ) : null}
      <div className="ml-auto flex flex-wrap gap-2">
        {canCreateContact && sender ? (
          <Button asChild variant="outline" size="sm">
            <Link
              to="/contacts/create"
              state={{ record: contactDraftFromSender(sender, company_ids[0]) }}
            >
              <UserPlus className="size-4" />
              {translate("crm.mail.emails.create_contact")}
            </Link>
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Link2 className="size-4" />
          {translate("crm.mailbox.add_to_feed")}
        </Button>
      </div>
      <Dialog
        open={isAdding}
        onOpenChange={(open) => !open && setAdding(false)}
      >
        <DialogContent className="sm:max-w-lg">
          {/* Mounted with the dialog, so each opening starts empty. */}
          {isAdding ? (
            <AddToFeedForm message={message} onClose={() => setAdding(false)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const AddToFeedForm = ({
  message,
  onClose,
}: {
  message: MailboxMessage;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const add = useAddMailboxMessageToCrm();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const { contact_ids, company_ids, partner_ids } = message.links;
  const hasMatches =
    contact_ids.length + company_ids.length + partner_ids.length > 0;
  const canAdd = hasMatches || companies.length + contacts.length > 0;

  const chosen = [
    ...companies.map((company) => ({
      key: `company-${company.id}`,
      label: company.name,
      remove: () =>
        setCompanies(companies.filter((item) => item.id !== company.id)),
    })),
    ...contacts.map((contact) => ({
      key: `contact-${contact.id}`,
      label: contactName(contact),
      remove: () =>
        setContacts(contacts.filter((item) => item.id !== contact.id)),
    })),
  ];

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("crm.mailbox.add_dialog.title")}</DialogTitle>
        <DialogDescription>
          {translate("crm.mailbox.add_dialog.help")}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap gap-2">
        <RecordPicker<Company>
          resource="companies"
          label={translate("crm.mailbox.add_dialog.company")}
          icon={<Building2 className="size-4" />}
          searchPlaceholder={translate("crm.mailbox.add_dialog.search")}
          emptyText={translate("crm.filters.no_match")}
          excludedIds={[...company_ids, ...companies.map((item) => item.id)]}
          sort={{ field: "name", order: "ASC" }}
          renderOption={(company) => (
            <span className="truncate">{company.name}</span>
          )}
          onSelect={(company) => setCompanies([...companies, company])}
        />
        <RecordPicker<Contact>
          resource="contacts"
          label={translate("crm.mailbox.add_dialog.contact")}
          icon={<User className="size-4" />}
          searchPlaceholder={translate("crm.mailbox.add_dialog.search")}
          emptyText={translate("crm.filters.no_match")}
          excludedIds={[...contact_ids, ...contacts.map((item) => item.id)]}
          sort={{ field: "last_name", order: "ASC" }}
          renderOption={(contact) => (
            <span className="truncate">{contactName(contact)}</span>
          )}
          onSelect={(contact) => setContacts([...contacts, contact])}
        />
      </div>
      {chosen.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {chosen.map((item) => (
            <li key={item.key}>
              <Badge variant="secondary" className="gap-1 font-normal">
                {item.label}
                <button
                  type="button"
                  onClick={item.remove}
                  aria-label={translate("crm.mailbox.add_dialog.remove", {
                    name: item.label,
                  })}
                  className="cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          disabled={!canAdd || add.isPending}
          onClick={() =>
            add.mutate(
              {
                folder: message.folder,
                uid: message.uid,
                company_ids: companies.map((item) => item.id),
                contact_ids: contacts.map((item) => item.id),
              },
              { onSuccess: onClose },
            )
          }
        >
          {translate("crm.mailbox.add_dialog.confirm")}
        </Button>
      </DialogFooter>
    </>
  );
};
